'use client';

import * as React from 'react';
import { InfoIcon } from 'lucide-react';

import { PricingTable } from '@workspace/billing/components/pricing-table';

import { trackCta } from '~/lib/track-cta';
import { SiteHeading } from '../fragments/site-heading';

interface PricingSectionProps {
  title?: string;
  description?: string;
}

/**
 * Homepage/tool-page pricing section. Renders the shared, config-driven
 * redesigned pricing cards (monthly + yearly + one-time), so prices stay in
 * sync with the app and the dedicated /pricing page. Cards link to sign-up
 * (no `onUpgrade`).
 */
const PricingSection: React.FC<PricingSectionProps> = ({
  title,
  description
}) => {
  // One-shot pricing_viewed when the inline section scrolls into view.
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const viewedRef = React.useRef(false);
  React.useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !viewedRef.current) {
          viewedRef.current = true;
          trackCta('pricing_viewed', {
            surface: 'inline_section',
            path: window.location.pathname
          });
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-section-green relative overflow-hidden"
      id="pricing"
    >
      <div className="container">
        <SiteHeading
          badge="Pricing"
          title={title || 'Simple & Transparent Plans For Creators'}
          isSecondary
          description={
            description ||
            'Choose the plan that fits your needs. Start using VideoMule today!'
          }
        />

        <PricingTable
          recommendedProductId="plus"
          className="mt-6"
          onPlanCtaClick={(productId, planId, interval) =>
            trackCta('plan_cta_clicked', {
              product_id: productId,
              plan_id: planId,
              interval,
              surface: 'inline_section',
              path: window.location.pathname
            })
          }
        />

        <div className="mt-8 flex items-start justify-center text-center text-sm text-muted-foreground md:items-center">
          <InfoIcon
            className="mr-1 inline-flex shrink-0"
            size={14}
          />
          <span>
            All pricing is in USD. You can change plans or cancel your account
            at any time, prior to renewal.
          </span>
        </div>
      </div>

      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-gray-300 to-transparent"></div>
    </section>
  );
};

export default PricingSection;
